import { useCallback, useState } from "react";
import { toast } from "react-toastify";

import useMqttApi from "hooks/use-mqtt-api";
import { RequestTopics } from "services/topics";

export enum StorageMethod {
  Put = "PUT",
  Get = "GET",
  GetAll = "GET_ALL",
  GetKeys = "GET_KEYS",
  Delete = "DELETE",
  DeleteAll = "DELETE_ALL",
  GetSize = "GET_SIZE",
  Clear = "CLEAR",
  Contains = "CONTAINS",
  IsEmpty = "IS_EMPTY",
}

export const useLocalStorage = <T = any>(
  key: string,
  initialValue: any
): [T, (value: any) => void] => {
  const [storedValue, setStoredValue] = useState<T>(initialValue);

  // Fetch function for getting data from local storage
  const { fetch: get } = useMqttApi({
    requestTopic: RequestTopics.LocalStorage,
    request: {
      method: StorageMethod.Get,
      body: key,
    },
    onSuccess: (data) => setStoredValue(data?.data ?? null),
  });

  // Fetch function for putting data into local storage
  const {
    fetch: put,
    error,
    loading,
  } = useMqttApi({
    requestTopic: RequestTopics.LocalStorage,
    enabled: false,
  });

  const setValue = useCallback(
    (value: any) => {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;

      if (loading) return;

      if (error) {
        get(); // Retry fetching to sync with actual local storage
        console.error(error);
        toast.error(error.message);
        return;
      }

      // Optimistic update: Update stored value before confirming with the server
      setStoredValue(valueToStore);

      // Send a request to update the local storage
      put({
        method: StorageMethod.Put,
        body: {
          [key]: valueToStore,
        },
      });
    },
    [error, loading]
  );

  return [storedValue as T, setValue];
};
